import React, { useCallback, useState } from 'react';
import { useRoadNamesById } from '../../../../hooks/useRoadDirectionLabels';
import { formatPK } from '../../../../utils/surveillanceRoute';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Components
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';

// Types
import { ShowOTRouteSegmentProps } from './types';

// Styled
import {
  ContainerContent,
  ContainerData,
  ContainerHeader,
  ContainerRefIcon,
  ContainerTitleArrow,
  DateContainer,
  InputAction,
  InputDescription,
  RefRoid,
  RightArrow,
  TagsContainer,
  TitleDate,
  TitleOrder,
  ActionLocationGroup,
} from '../MainStyledComponents';
import {
  ContainerOTStyled,
  PauseCauseChip,
  DescriptionRow,
  PhotosRow,
  PhotosHeader,
  PhotosAddButton,
} from './styles';
import {
  RoadStretchList,
  RoadStretchRows,
  RoadStretchLabel,
  RoadStretchRow,
  RoadStretchName,
  RoadStretchRange,
} from '../MainStyledComponents';

// Components
import { SpecialTag } from '../../SpecialTag';
import { Typography } from '../../Typography';
import { MainButton } from '../../../inputs/buttons/MainButton';
import { GridOfPhotos } from '../../../multimedia/GridOfPhotos';
import { TagActionsMenu } from '../../SpecialTag/TagActionsMenu';
import { variant } from '../../SpecialTag/types';
import { LinkedWorkTags } from '../../LinkedWorkTags';
import { DateTime } from '../../../inputs/DateTime';

// Modal (V4 D6 — chip causa → ChangePauseCauseModal, gesto ChangeStateModal)

// Hooks
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { useChangeSegmentPauseCause } from '../../../../hooks/useChangeSegmentPauseCause';
import { useSegmentPhotos } from '../../../../hooks/useSegmentPhotos';
import { useImagesByIds } from '../../../../hooks/useImagesByIds';
import { useDeleteSegmentPhoto } from '../../../../hooks/useDeleteSegmentPhoto';

// Utils
import {
  directionNameKey,
  isSegmentCauseChangeable,
  laneName,
} from '../../../../utils/surveillanceRoute';
import { mapToGridImages } from '../../ActionContentCardResume/mapToGridImages';

// Icons
import ArrowRight from '../../../../assets/icons/triangle-right.svg';

/**
 * V4 P8 FR-8.3 — Card del Tramo (RouteSegment) en la tab "Ordres de Treball".
 *
 * Copia adaptada de `ActionOT` al schema `RouteSegment+Order` (entidad doble).
 * Diferencias respecto a `ActionOT`:
 * - Header muestra `Tr.NN | hora inici – hora fi | PK inici → PK fi`.
 * - Chip `pauseCause` con color semántico (azul / rojo / ámbar — D7).
 * - Campos específicos: `pauseCauseDetail`, `direction`, `lane`, PKs.
 * - Pills cross-link (D2). Estado Fase 1 (2026-07-08, P15 wiring — ver
 *   `docs/superpowers/specs/2026-07-08-p15-vincular-tram-wiring-design.md`):
 *   - "Vincular a actuació" (`pauseCause=action`, sin vinculada) → DIFERIDO a
 *     Fase 2 (necesita modo "elige actuació" en el modal). No se renderiza
 *     aún — su guard (`onRelateClick` truthy) nunca lo activa el consumidor.
 *   - "Resolt per At.NN" (`pauseCause=action`, vinculada) → **navega** al
 *     detail de la actuació vinculada (Fase 1, `onRelateClick` reutilizado
 *     como "click en pill ya vinculada").
 *   - "Pendent d'Avís a sala" (ámbar, `pendingExternalLink=true`) — informativo,
 *     sin acción (la sala lo introduce desde su wizard — Fase 2).
 *   - "Vinculat amb OT.NN" (external vinculada) → navega al detail de la OT externa.
 *
 * D6 (2026-07-06, feedback usuario): la fila es 100% read-only como las hijas
 * de Work Orders. La causa se corrige clicando el CHIP "Causa: X" → abre
 * `ChangePauseCauseModal` (mismo gesto que chip de estado → ChangeStateModal
 * en WorkOrder). Solo tramos cerrados y roles con permiso sobre el RV; el
 * backend reconcilia vínculos y si la causa pasa a `action` se encadena la
 * creación de la Actuació básica (misma cadena FR-13.3 que el modal P13).
 */
export const RouteSegmentOT = ({
  roid,
  tag,
  date,
  endDate,
  pkInit,
  pkEnd,
  direction,
  lane,
  // `description` (Order gemela) ya no se desestructura: la card del tramo no
  // la pinta desde 2026-08-10. El prop sigue en el tipo compartido de ShowOT.
  pauseCause,
  pauseCauseDetail,
  pendingExternalLink,
  pendingActionLink,
  linkedOrderRoid,
  onRelateClick,
  onLinkExternalClick,
  onEditExternalClick,
  onUnlinkExternalClick,
  onLinkedExternalClick,
  linkedExternalWorks,
  onLinkedWorkClick,
  canCreateBasicAction,
  id,
  parentOrder,
  revalidatorFunction,
  onEditClick,
  imageIDs,
  orderId,
  canAttachPhotos: canAttachPhotosProp,
  canChangeCause = false,
  roadStretches,
  isTransferred = false,
  transferredFrom,
}: ShowOTRouteSegmentProps): JSX.Element => {
  // Los nombres salen del MISMO sitio que la cabecera del recorrido (datos
  // maestros vía `GET_ROADS`, cache-first): la pantalla de detalle no precacheó
  // carreteras hasta que ese hook dejó de ser `cache-only`, y con la caché fría
  // esto se quedaba vacío en silencio.
  const stretchRoadIds = (roadStretches ?? []).map((st) => st.roadId);
  const stretchRoadNames = useRoadNamesById(stretchRoadIds);
  const { t } = useTranslation(['pages', 'common', 'forms']);

  // 🔴 La carretera se enseña SIEMPRE, cruzara o no (feedback usuario
  // 2026-08-24). Antes un tramo de una sola vía enseñaba dos PK sueltos y
  // ninguna carretera: en un recorrido de varias eso no se puede deducir de la
  // cabecera, así que no se sabía de cuál eran esos números. El selector
  // normaliza el caso de una sola a un trozo, así que aquí hay UNA sola forma.
  const roadRows = (roadStretches ?? []).map((st, i) => ({
    key: `${st.roadId}-${i}`,
    name: stretchRoadNames[st.roadId] || st.roadId,
    range: `(${formatPK(st.pkInitKm, st.pkInitMeter)} → ${formatPK(
      st.pkEndKm,
      st.pkEndMeter
    )})`,
  }));
  const { pathname } = useLocation();
  const isLandscapeTablet = useIsLandscapeTablet();
  const [showInfo, setShowInfo] = useState(false);
  const toggleInfo = useCallback(() => setShowInfo((prev) => !prev), []);

  // ── Fotos del tramo (intervención leve, 2026-08-09) ──────────────────────
  // Las ya guardadas se resuelven SOLO al desplegar la card; las nuevas van
  // por la misma cadena que el cierre del tramo (`useSegmentPhotos`), así que
  // el gesto y el encolado son uno solo en los dos puntos de entrada.
  const { openPhotoChain } = useSegmentPhotos();
  const segmentImages = useImagesByIds(imageIDs, showInfo);
  // Borrar una foto: papelera en la miniatura → confirmación → se aplica ya.
  // Solo para quien puede adjuntarlas — el mismo permiso gobierna las dos
  // acciones, que son las dos caras de lo mismo.
  const { requestDelete } = useDeleteSegmentPhoto({
    orderId,
    onDeleted: revalidatorFunction,
  });
  const canAttachPhotos = !!orderId && !!canAttachPhotosProp;
  // El bloque existe si hay algo que enseñar O algo que añadir: ver las fotos
  // no es editarlas, así que quien no puede añadir sigue viéndolas.
  const showPhotosBlock = canAttachPhotos || segmentImages.length > 0;
  const handleAddPhotos = useCallback(() => {
    if (!orderId || !id) return;
    openPhotoChain({
      segmentId: id,
      orderId,
      // El ROID del tramo (`Tr.<exp>.<type>.02`) es la fuente de los índices:
      // la card no los recibe sueltos, pero sí el ROID ya formado.
      indexExpedition: Number(roid?.split('.')?.[1] ?? 0),
      indexType: Number(roid?.split('.')?.[2] ?? 0),
      // Cuántas tiene ya: la numeración de la tanda nueva arranca DESPUÉS, o
      // el dedupe de la cola borraría las anteriores que sigan pendientes.
      existingPhotos: imageIDs?.length ?? 0,
    });
  }, [openPhotoChain, orderId, id, roid, imageIDs]);

  const formatTimeOrDash = (ts?: number): string =>
    ts ? format(new Date(ts), 'HH:mm') : '—';

  // D6: la causa solo se cambia en tramos CERRADOS y con causa (el backend
  // lo exige), por GESTIÓN y con el recorrido sin cerrar — lo decide la página
  // (`canChangeCause`), con la misma regla que la columna «Cambiar causa» de
  // la tabla de Tramos, que abre el mismo modal.
  const canEditCause =
    canChangeCause &&
    // `tag` = estado de la Order gemela: el back anula las dos mitades.
    isSegmentCauseChangeable({ id, endDate, pauseCause, state: tag });

  // La card solo se pinta bajo `/surveillance-route/:routeId/...`.
  const routeId = pathname.match(/\/surveillance-route\/([^/]+)/)?.[1];
  const { openChangeCause, launchBasicActionChain } =
    useChangeSegmentPauseCause(revalidatorFunction);

  const openModalChangeCause: React.MouseEventHandler<HTMLSpanElement> = (
    event
  ) => {
    event.stopPropagation();
    if (!canEditCause || !id || !pauseCause) return;
    openChangeCause({
      segmentId: id,
      routeId,
      parentOrder,
      currentCause: pauseCause,
      currentDetail: pauseCauseDetail,
      linkedOrderRoid,
      // «Actuación» solo si aquí se puede crear la básica: la misma condición
      // que la pastilla «Vincular a actuación» de esta card.
      canChooseAction: Boolean(canCreateBasicAction),
    });
  };

  return (
    <ContainerOTStyled>
      <ContainerHeader onClick={toggleInfo}>
        <TitleOrder>
          {t('pages:surveillance.segment')}
          <TitleDate>
            {/* Patrón de título de las filas hijas de WO (feedback usuario
                2026-07-03): "Actuación 14/05/2026 - 15:33 a 16:00". Los PKs
                viven en el detalle expandido, no en el título. */}
            {` ${format(new Date(date), 'dd/MM/yyyy')} - ${formatTimeOrDash(date)}${endDate ? ` a ${formatTimeOrDash(endDate)}` : ''}`}
          </TitleDate>
        </TitleOrder>
        <ContainerTitleArrow>
          <ContainerRefIcon>
            <RefRoid>{roid}</RefRoid>
            <TagsContainer>
              {pauseCause && (
                <PauseCauseChip
                  cause={pauseCause}
                  $small={isLandscapeTablet}
                  onClick={canEditCause ? openModalChangeCause : undefined}
                  style={canEditCause ? { cursor: 'pointer' } : undefined}
                >
                  {/* Prefijo "Causa:" (feedback usuario 2026-07-04): sin él el
                      chip se leía como el TIPO de la fila y no como el motivo
                      del paro del tramo. Keys existentes. */}
                  {`${t('pages:surveillance.pause.cause')}: ${t(`pages:surveillance.pause.causes.${pauseCause}`, pauseCause)}`}
                </PauseCauseChip>
              )}
              {/* UX 2026-07-07 (feedback usuario): "Resolt per At.NN" / "Pendent
                  d'Avís a sala" son etiquetas de ESTADO del tramo (igual que la
                  causa o el estado), no una acción — viven en el mismo grupo de
                  chips de cabecera, junto al chip de causa y el tag de estado
                  (antes colgaban sueltas debajo de la fila). `$chip` = misma
                  geometría pill que `PauseCauseChip`, solo cambia el color. */}
              {/* Pendent d'Avís a sala → pill ÁMBAR: es el ESTADO del aviso. La
                  acción (vincular a una OT) es la CTA granate de debajo
                  (2026-09-24), solo para quien puede ligar. */}
              {pendingExternalLink && (
                <SpecialTag
                  variant={variant.PendingLink}
                  sizeTag={isLandscapeTablet ? 'small' : 'default'}
                  title={t('pages:surveillance.pendingExternalLink')}
                />
              )}
              {/* CTAs del tramo (2026-09-24, feedback usuario): van DENTRO del
                  grupo de pills, justo detrás del estado del vínculo, como
                  «Vincular OT» en la card del recorrido. Antes colgaban en una
                  fila aparte bajo el título y quedaban descolocadas en todos
                  los tamaños. `stopPropagation`: la cabecera despliega la card. */}
              {pauseCause === 'external-call' &&
                !linkedOrderRoid &&
                onLinkExternalClick && (
                  <SpecialTag
                    variant={variant.LinkCta}
                    sizeTag={isLandscapeTablet ? 'small' : 'default'}
                    title={t('pages:surveillance.linkAviso')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onLinkExternalClick();
                    }}
                    style={{ cursor: 'pointer' }}
                    data-testid="segment-link-order-cta"
                  />
                )}
              {/* «Vincular a actuación» (tramo con causa Actuación sin básica):
                  sin vínculo lanza la MISMA cadena P9 que el cierre online —
                  crea la básica y entra en su wizard con `?fromSegment` —; con
                  vínculo, `onRelateClick` manda (2026-08-03). Fuente preferente
                  del «pendiente»: el flag del BACKEND; si no llega (offline o
                  back anterior) se deriva. */}
              {pauseCause === 'action' &&
                (pendingActionLink ?? !linkedOrderRoid) &&
                !linkedOrderRoid &&
                (onRelateClick || canCreateBasicAction) && (
                  <SpecialTag
                    variant={variant.LinkCta}
                    sizeTag={isLandscapeTablet ? 'small' : 'default'}
                    title={t('pages:surveillance.linkToAction')}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onRelateClick) onRelateClick();
                      else if (id)
                        launchBasicActionChain(
                          { segmentId: id, routeId, parentOrder },
                          pauseCauseDetail
                        );
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              {/* Vinculada → pill VERDE "Resolt per At.NN" / "Vinculat amb
                  OT.NN". Una sola acción (ver → navega a la entidad
                  vinculada) → TagActionsMenu la ejecuta con el tap sobre
                  TODA la pill (opción C, feedback usuario 2026-07-10). Es
                  ver-only: el re-vínculo se edita desde el lado actuació/OT,
                  no desde el tramo. Sin handler (contexto no navegable) →
                  tag informativo plano. */}
              {linkedOrderRoid &&
                (onLinkedExternalClick ||
                onRelateClick ||
                onEditExternalClick ||
                onUnlinkExternalClick ? (
                  <TagActionsMenu
                    variant={variant.LinkedFromSegment}
                    sizeTag={isLandscapeTablet ? 'small' : 'default'}
                    title={
                      pauseCause === 'action'
                        ? t('pages:surveillance.resolvedBy', {
                            roid: linkedOrderRoid,
                          })
                        : t('pages:surveillance.linkedWith', {
                            roid: linkedOrderRoid,
                          })
                    }
                    actions={[
                      ...(onLinkedExternalClick || onRelateClick
                        ? [
                            {
                              icon: 'view' as const,
                              label: t('common:buttons.view'),
                              onClick: () =>
                                (onLinkedExternalClick ?? onRelateClick)?.(),
                            },
                          ]
                        : []),
                      // Aviso a sala (2026-09-24): quien puede ligar corrige
                      // desde aquí — el aviso vive en el tramo. «Editar» sí
                      // cambia: un aviso va con UNA sola OT.
                      ...(onEditExternalClick
                        ? [
                            {
                              icon: 'edit' as const,
                              label: t('common:buttons.edit'),
                              onClick: () => onEditExternalClick(),
                            },
                          ]
                        : []),
                      ...(onUnlinkExternalClick
                        ? [
                            {
                              icon: 'unlink' as const,
                              label: t('common:buttons.unlink'),
                              onClick: () => onUnlinkExternalClick(),
                            },
                          ]
                        : []),
                    ]}
                  />
                ) : (
                  <SpecialTag
                    variant={variant.LinkedFromSegment}
                    sizeTag={isLandscapeTablet ? 'small' : 'default'}
                    title={
                      pauseCause === 'action'
                        ? t('pages:surveillance.resolvedBy', {
                            roid: linkedOrderRoid,
                          })
                        : t('pages:surveillance.linkedWith', {
                            roid: linkedOrderRoid,
                          })
                    }
                  />
                ))}
              {/* Actuación o accidente que atendió el aviso (spec 2026-10-27):
                  van pegadas a la pill de la OT, antes de «Transferido». */}
              <LinkedWorkTags
                works={linkedExternalWorks}
                onOpen={onLinkedWorkClick}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
                testId="segment-card-aviso-work"
              />
              {/* Revisión del 22-oct: la pastilla verde de las actuaciones y
                  comunicaciones transferidas, también en el tramo. Solo
                  informa (el traspaso se hace desde la tabla de Tramos). */}
              {isTransferred && (
                <SpecialTag
                  variant={variant.Transferred}
                  sizeTag={isLandscapeTablet ? 'small' : 'default'}
                  data-testid="segment-card-transferred"
                />
              )}
              <SpecialTag
                variant={tag}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
              />
            </TagsContainer>
          </ContainerRefIcon>
          <RightArrow
            src={ArrowRight}
            alt={t('common:expandInfo')}
            showInfo={showInfo}
          />
        </ContainerTitleArrow>
      </ContainerHeader>

      {showInfo && (
        <ContainerContent>
          <ContainerData>
            <ActionLocationGroup>
              <DateContainer>
                <DateTime
                  label={t('forms:workOrder.registerDate')}
                  labelHour={t('forms:workOrder.startHour')}
                  isDisabled={true}
                  initialDate={date}
                />
                {endDate && (
                  <DateTime
                    labelHour={t('forms:workOrder.endHour')}
                    onlyHour={true}
                    isDisabled={true}
                    initialDate={endDate}
                  />
                )}
              </DateContainer>

              {direction && (
                <InputAction
                  label={t('forms:workOrder.direction')}
                  typeInput="normal"
                  disabled={true}
                  value={t(
                    directionNameKey(direction) ??
                      'pages:surveillance.directionUnspecified'
                  )}
                />
              )}

              {lane && (
                <InputAction
                  label={t('forms:workOrder.lane')}
                  typeInput="normal"
                  disabled={true}
                  value={laneName(lane)}
                />
              )}

              {/* Sin carretera que enseñar (tramos anteriores al
                  multi-carretera, que no la guardaban) quedan las cajas de PK
                  de siempre: su recorrido tenía una sola vía y la dice la
                  cabecera. Enseñar una carretera adivinada sería peor. */}
              {roadRows.length === 0 && (
                <>
                  {pkInit && (
                    <InputAction
                      label={t('forms:workOrder.pkInitial')}
                      typeInput="normal"
                      disabled={true}
                      value={pkInit}
                    />
                  )}

                  {pkEnd && (
                    <InputAction
                      label={t('forms:workOrder.pkFinal')}
                      typeInput="normal"
                      disabled={true}
                      value={pkEnd}
                    />
                  )}
                </>
              )}

              {/* D6 (2026-07-06): la causa se corrige clicando el CHIP del
                  header (→ ChangePauseCauseModal). En el detalle expandido es
                  read-only como el resto de campos, patrón de las filas hijas. */}
              {pauseCause && (
                <InputAction
                  label={t('pages:surveillance.pause.cause')}
                  typeInput="normal"
                  disabled={true}
                  value={t(
                    `pages:surveillance.pause.causes.${pauseCause}`,
                    pauseCause
                  )}
                />
              )}

              {/* Las vías van LAS ÚLTIMAS y en horizontal (2026-08-18): es
                  el único campo que puede ocupar más de una línea, así que en
                  medio de la fila empujaba al resto y dejaba huecos. Al final
                  no estorba a nadie y la causa se queda arriba con los demás. */}
              {roadRows.length > 0 && (
                <RoadStretchList>
                  <RoadStretchLabel>
                    {t('forms:workOrder.road')}
                  </RoadStretchLabel>
                  <RoadStretchRows>
                    {roadRows.map((fila) => (
                      <RoadStretchRow key={fila.key}>
                        <RoadStretchName>{fila.name}</RoadStretchName>
                        <RoadStretchRange>{fila.range}</RoadStretchRange>
                      </RoadStretchRow>
                    ))}
                  </RoadStretchRows>
                </RoadStretchList>
              )}

              {/* De dónde vino (revisión del 22-oct). El ↪ de la tabla de
                  Tramos solo lo explica al pasar el ratón; en tablet, aquí.
                  Con la forma de la carretera: una casilla de una línea
                  cortaba el texto. */}
              {transferredFrom && (
                <RoadStretchList data-testid="segment-card-transferred-from">
                  <RoadStretchLabel>
                    {t('pages:surveillance.transferSegment.from')}
                  </RoadStretchLabel>
                  <RoadStretchRows>
                    <RoadStretchRow>
                      <RoadStretchName>
                        {transferredFrom.fromRouteRoid ?? '—'}
                      </RoadStretchName>
                      <RoadStretchRange>
                        {`(${transferredFrom.fromRoid ?? '—'} · ${format(
                          new Date(transferredFrom.at),
                          'dd/MM/yyyy HH:mm'
                        )})`}
                      </RoadStretchRange>
                    </RoadStretchRow>
                  </RoadStretchRows>
                </RoadStretchList>
              )}

              {/* La descripción del tramo NO se pinta aquí: es texto libre y
                  se merece el ancho del bloque de abajo, no una casilla de una
                  línea entre PKs y causa (2026-08-10). */}
            </ActionLocationGroup>

            {/* Corregir el tramo (2026-08-05) — descripción y lápiz EN LA
                MISMA FILA, calcado de la card de la Orden de Trabajo: el
                textarea cede el ancho del botón y el lápiz se apoya en su
                esquina inferior derecha.

                El lápiz vive aquí, dentro del bloque desplegado, y no en la
                cabecera: allí se veía con la card plegada y competía con los
                chips de causa y estado. Al no colgar de `ContainerHeader` no
                hace falta `stopPropagation` para que no pliegue la card.

                La fila se pinta si hay descripción O si hay lápiz: un tramo
                puede no tener descripción y el botón debe seguir saliendo.

                2026-08-10 — aquí va la descripción del TRAMO, que es su
                `pauseCauseDetail`: el mismo dato que pintan la tabla de Tramos,
                el informe y el modal de sala, y con el mismo nombre. Ocupa el
                ancho del bloque porque es texto libre.

                Ya NO se pinta `description` (la Order gemela). Era la vieja
                «descripción de arranque», que dejó de pedirse: en los tramos
                nuevos siempre viene vacía, y en los antiguos duplicaba —
                medido en beta, 85 de los 123 tramos con ambos campos decían
                literalmente lo mismo—. El dato NO se borra; solo deja de
                pintarse, y ninguna otra superficie lo mostraba nunca. */}
            {(pauseCauseDetail || onEditClick) && (
              <DescriptionRow>
                {pauseCauseDetail && (
                  <InputDescription
                    label={t('pages:surveillance.pauseCauseDetail')}
                    value={pauseCauseDetail}
                    disabled={true}
                  />
                )}
                {onEditClick && (
                  <ButtonIconBackground
                    type="button"
                    variant="edit"
                    title={t('pages:surveillance.editSegment.action')}
                    // La card GEMELA del RV, en esta misma pestaña, tiene su
                    // propio lápiz (navega al wizard EditRoute) con el MISMO
                    // `alt="icona da llapis"`. Sin un testid propio, un
                    // `locator('img[alt=...]').first()` engancharía uno u otro
                    // según el orden del árbol.
                    data-testid="segment-card-edit"
                    onClick={onEditClick}
                  />
                )}
              </DescriptionRow>
            )}

            {/* Fotos del tramo (2026-08-09). Segundo punto de entrada a la
                MISMA cadena que ofrece el cierre con causa «Intervenció lleu»:
                cubre el olvido, y es la única vía cuando el cierre se encoló
                sin cobertura y el modal no llegó a salir.

                Se pinta con el sistema de siempre —vacío: botón con texto; con
                contenido: la rejilla y el «+» al lado— igual que Actuación y
                Accidente. Las guardadas van por `GridOfPhotos`, que ya trae
                pantalla completa y descarga de la selección. */}
            {showPhotosBlock && (
              <PhotosRow>
                <PhotosHeader>
                  <Typography component="h3" variant="semiBold" size="L">
                    {t('forms:actionEdition.photos')}
                  </Typography>
                  {segmentImages.length > 0 && canAttachPhotos && (
                    <PhotosAddButton
                      type="button"
                      variant="add"
                      data-testid="segment-card-add-photos"
                      title={t('forms:actionEdition.addImages')}
                      onClick={handleAddPhotos}
                    />
                  )}
                </PhotosHeader>
                {/* Vacío: el mismo `MainButton` que usa Actuación. Con fotos:
                    el `+` redondo pasa al lado del título y la rejilla queda
                    debajo, que es como la app enseña fotos ya subidas en un
                    detalle (`ActionContentCardResume`). */}
                {segmentImages.length === 0 ? (
                  <MainButton
                    type="button"
                    data-testid="segment-card-add-photos-empty"
                    text={t('forms:actionEdition.addImages')}
                    onClick={handleAddPhotos}
                  />
                ) : (
                  <GridOfPhotos
                    imagesToShow={mapToGridImages(segmentImages, roid)}
                    onDeletePhoto={
                      canAttachPhotos
                        ? (idFoto) => {
                            const foto = segmentImages.find(
                              (img) => img.id === idFoto
                            );
                            if (foto) {
                              requestDelete({ id: foto.id, name: foto.name });
                            }
                          }
                        : undefined
                    }
                  />
                )}
              </PhotosRow>
            )}
          </ContainerData>
        </ContainerContent>
      )}
    </ContainerOTStyled>
  );
};

export default RouteSegmentOT;
